import {useCallback, useEffect, useState} from 'react';

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
function authHeaders() {
    const t = localStorage.getItem('access_token');
    return t ? {'Authorization': `Bearer ${t}`} : {};
}

/** Stable client-side key for a non-FINTEL unified hit (corpus / data_item). */
function unifiedHitKey(hit) {
    const t = hit?.openTarget;
    if (hit?.documentStore === 'corpus' && t?.docId != null) {
        return `corpus:${t.docId}:${t.segmentId ?? ''}`;
    }
    if (hit?.documentStore === 'data_item') {
        return `data_item:${t?.canonicalFileId ?? ''}:${t?.referenceId ?? ''}`;
    }
    return `${hit?.documentStore}:${JSON.stringify(t)}`;
}

/** localStorage key for the read-set of a given feed. */
const storageKey = (feedId) => `feeds:read:${feedId}`;

/** Load the set of hitKeys that have been read (persisted in localStorage). */
function loadReadSet(feedId) {
    if (!feedId) return new Set();
    try {
        const raw = localStorage.getItem(storageKey(feedId));
        if (!raw) return new Set();
        const parsed = JSON.parse(raw);
        return new Set(Array.isArray(parsed) ? parsed : []);
    } catch {
        return new Set();
    }
}

/** Persist an updated read-set to localStorage. */
function saveReadSet(feedId, readSet) {
    if (!feedId) return;
    try {
        localStorage.setItem(storageKey(feedId), JSON.stringify([...readSet]));
    } catch {
        // ignore quota errors
    }
}

const useFeedItems = (feedId, userId, refreshKey = 0) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        if (!feedId) { setItems([]); setLoading(false); return; }
        setLoading(true);
        const qs = userId ? `?userId=${encodeURIComponent(userId)}` : '';
        const readSet = loadReadSet(feedId);

        // Fetch FINTEL items (with read/unread state) and unified corpus+dataitems in parallel.
        // Filter unified results to corpus/data_item only — report hits would duplicate FINTELs.
        Promise.all([
            fetch(`${BASE}/api/feeds/${encodeURIComponent(feedId)}/fintel-items${qs}`, {headers: authHeaders()})
                .then(r => r.ok ? r.json() : []),
            fetch(`${BASE}/api/feeds/${encodeURIComponent(feedId)}/items-unified${qs}`, {headers: authHeaders()})
                .then(r => r.ok ? r.json() : {items: []})
                .then(resp => (resp?.items ?? []).filter(h => h.documentStore !== 'report')),
        ])
            .then(([fintelItems, unifiedHits]) => {
                // Normalise unified hits to a shape FeedItemStack can render.
                // Restore read state from localStorage so unread persists across refreshes.
                const normalised = unifiedHits.map(h => {
                    const hitKey = unifiedHitKey(h);
                    return {
                        ...h,
                        _hitKey: hitKey,
                        type: h.documentStore,          // 'corpus' | 'data_item'
                        title: h.title || '(untitled)',
                        published: h.documentDate || null,
                        matchedSegmentText: h.snippet || null,
                        read: readSet.has(hitKey),
                    };
                });
                setItems([
                    ...(Array.isArray(fintelItems) ? fintelItems : []),
                    ...normalised,
                ]);
                setLoading(false);
            })
            .catch((err) => { setError(err); setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feedId, userId, refreshKey]);

    useEffect(() => { load(); }, [load]);

    /** Mark a FINTEL report as read (persisted to backend). */
    const markRead = useCallback((reportId) => {
        if (!feedId || !userId || !reportId) return;
        setItems(prev => prev.map(it => it.reportId === reportId ? {...it, read: true} : it));
        fetch(`${BASE}/api/feeds/${encodeURIComponent(feedId)}/fintel-items/${encodeURIComponent(reportId)}/read?userId=${encodeURIComponent(userId)}`, {
            method: 'POST', headers: authHeaders(),
        }).catch(() => {});
    }, [feedId, userId]);

    /** Mark a FINTEL report as unread (persisted to backend). */
    const markUnread = useCallback((reportId) => {
        if (!feedId || !userId || !reportId) return;
        setItems(prev => prev.map(it => it.reportId === reportId ? {...it, read: false} : it));
        fetch(`${BASE}/api/feeds/${encodeURIComponent(feedId)}/fintel-items/${encodeURIComponent(reportId)}/read?userId=${encodeURIComponent(userId)}`, {
            method: 'DELETE', headers: authHeaders(),
        }).catch(() => {});
    }, [feedId, userId]);

    /** Mark a corpus / data-item hit as read (persisted to localStorage). */
    const markHitRead = useCallback((hitKey) => {
        if (!hitKey) return;
        setItems(prev => prev.map(it => it._hitKey === hitKey ? {...it, read: true} : it));
        const readSet = loadReadSet(feedId);
        readSet.add(hitKey);
        saveReadSet(feedId, readSet);
    }, [feedId]);

    /** Mark a corpus / data-item hit as unread (persisted to localStorage). */
    const markHitUnread = useCallback((hitKey) => {
        if (!hitKey) return;
        setItems(prev => prev.map(it => it._hitKey === hitKey ? {...it, read: false} : it));
        const readSet = loadReadSet(feedId);
        readSet.delete(hitKey);
        saveReadSet(feedId, readSet);
    }, [feedId]);

    return {items, loading, error, markRead, markUnread, markHitRead, markHitUnread};
};

export default useFeedItems;
